/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import useRouter from 'js/lib/useRouter';

import { Search, SelectOption, SilentSelectField, breakpoints } from '@coursera/cds-core';
import type { Country } from '@coursera/grpc-types-countrynames/coursera/proto/countrynames/v1/country_names';

import _t from 'i18n!nls/partners-graphql';

type SortOrder = '' | 'asc' | 'desc';

type Props = {
  countries: Country[];
  selectedCountry: string;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  sortOrder: SortOrder;
  onSortChange: (order: SortOrder) => void;
};

const styles = {
  row: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-200);
    margin: var(--cds-spacing-200) 0;

    ${breakpoints.down('sm')} {
      flex-wrap: wrap;
      gap: var(--cds-spacing-100);
    }
  `,
  search: css`
    flex: 1;
    max-width: 400px;
    min-width: 200px;

    ${breakpoints.down('sm')} {
      max-width: none;
      width: 100%;
      flex-basis: 100%;
    }
  `,
  sortContainer: css`
    margin-left: auto;
  `,
};

const PartnerFiltersRow = ({
  countries,
  selectedCountry,
  searchQuery,
  onSearchChange,
  sortOrder,
  onSortChange,
}: Props) => {
  const router = useRouter();

  const handleCountryChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;
    if (value) {
      router.push(`/partners?countryCode=${value.toLowerCase()}`);
    } else {
      router.push('/partners');
    }
  };

  const handleSortChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    onSortChange(event.target.value as SortOrder);
  };

  return (
    <div css={styles.row} data-testid="partner-filters-row">
      <div css={styles.search}>
        <Search
          value={searchQuery}
          fullWidth
          onChange={onSearchChange}
          hideSearchButton
          placeholder={_t('Search partners')}
        />
      </div>

      <SilentSelectField
        label={_t('Filter country')}
        labelPlacement="none"
        placeholder={_t('Filter country')}
        value={selectedCountry?.toUpperCase() || ''}
        onChange={handleCountryChange}
      >
        {[
          <SelectOption key="all" value="">
            {_t('Filter country')}
          </SelectOption>,
          ...countries.map((country) => (
            <SelectOption key={country.isoAlpha2Code} value={country.isoAlpha2Code}>
              {country.name}
            </SelectOption>
          )),
        ]}
      </SilentSelectField>

      <div css={styles.sortContainer}>
        <SilentSelectField
          label={_t('Sort')}
          labelPlacement="none"
          placeholder={_t('Sort')}
          value={sortOrder}
          onChange={handleSortChange}
        >
          {[
            <SelectOption key="asc" value="asc">
              {_t('A–Z')}
            </SelectOption>,
            <SelectOption key="desc" value="desc">
              {_t('Z–A')}
            </SelectOption>,
          ]}
        </SilentSelectField>
      </div>
    </div>
  );
};

export default PartnerFiltersRow;
